import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals of the three dashboard-catalogue mutations: PCG-79.
 *
 * It reuses `refusalCode` and `Refusal` rather than restating them: the shape of a
 * refusal and the way it travels in `extensions.code` are properties of the API, not
 * of a screen, and a second copy would drift from the first the day a code is added.
 *
 * **Attention, the API's own refusal strings are French here but they are the service's, not
 * the operator's.** They name columns and invariants: « le fabricant de jeton
 * refuse de signer », « unique plein », which is the right register for a log and
 * the wrong one for somebody trying to declare a dashboard. So the wording below is
 * the front's, and each entry says what is true and what to do about it, never
 * « une erreur est survenue ».
 *
 * **Attention, never « réessayez » unless retrying is genuinely the fix.**
 * `DASHBOARD_ALREADY_DECLARED` is permanent by construction: the place is occupied until
 * somebody archives what sits in it, and a second click answers identically.
 */

/**
 * Attention, LE DISCRIMINATEUR `Catalogue` A DISPARU AVEC LES LISTES DE PARTENAIRES
 * (PCG-276), et il n'existait que pour `MISSING_PERMISSION` : le droit d'un tableau de
 * bord est `manage_accounts`, celui d'une liste `manage_referential`, et les deux ne sont
 * pas portes par les memes personnes. Une fonction pour deux publics imposait aussi des
 * formulations de compromis partout ailleurs. Le vocabulaire des listes vit desormais dans
 * `partners/refusals.ts`.
 */

/** The refusals common to the three dashboard gestures. */
const commonRefusal = (code: string | null): Refusal | null => {
    switch (code) {
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Catalogue refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Catalogue refusal').t`Sign in again, then start the declaration over.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Catalogue refusal').t`This gesture belongs to the back-office.`,
                guidance: c('Catalogue refusal')
                    .t`Declaring what a client may consult is a Freelance.com decision, so a client-side role cannot carry it whatever its permissions. The scope of the role is checked before the permission, which is why a custom role holding the right is refused too.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Catalogue refusal').t`Your role does not let you manage accounts.`,
                guidance: c('Catalogue refusal')
                    .t`A dashboard is the object of an account, so declaring one takes the same permission as opening that account to the portal. Ask an administrator for it, or ask them to declare the entry.`,
            };
        case 'EMPTY_CATALOGUE_NAME':
            return {
                title: c('Catalogue refusal').t`A catalogue entry carries a name.`,
                guidance: c('Catalogue refusal')
                    .t`It is the label the client reads on the entry. Type one and send again.`,
            };
        default:
            return null;
    }
};

/** The refusals of the three dashboard mutations. */
const dashboardRefusal = (code: string | null): Refusal | null => {
    switch (code) {
        case 'FORBIDDEN_OUT_OF_SCOPE':
            return {
                title: c('Catalogue refusal').t`This account is outside your portfolio.`,
                guidance: c('Catalogue refusal')
                    .t`Declaring a dashboard on an account is reserved to whoever carries it. Ask an administrator whose portfolio holds this account.`,
            };
        case 'ORGANIZATION_NOT_FOUND':
            return {
                title: c('Catalogue refusal').t`This account no longer exists.`,
                guidance: c('Catalogue refusal')
                    .t`It has probably disappeared from the source since this screen was loaded. Reload the page to see the accounts as they are now.`,
            };
        case 'INTERNAL_ORGANIZATION_NOT_ALLOWED':
            return {
                title: c('Catalogue refusal').t`Freelance.com is not a client account.`,
                guidance: c('Catalogue refusal')
                    .t`The internal organisation carries no client dashboard. Internal reporting does not go through this catalogue.`,
            };
        case 'INVALID_METABASE_DASHBOARD_ID':
            return {
                title: c('Catalogue refusal').t`The Metabase identifier is not a positive whole number.`,
                guidance: c('Catalogue refusal')
                    .t`It is the number Metabase gives the dashboard, the one that appears in its address inside Metabase. Correct it and send again.`,
            };
        case 'DASHBOARD_PERIMETER_PARAM_REQUIRED':
            return {
                title: c('Catalogue refusal').t`This entry declares no perimeter parameter.`,
                guidance: c('Catalogue refusal')
                    .t`Without the slug of the locked parameter carrying the account, the token maker refuses to sign: the habilitation would be granted and would never open anything. Fill that field — it is the one thing this declaration cannot do without.`,
            };
        case 'DASHBOARD_ALREADY_DECLARED':
            return {
                title: c('Catalogue refusal').t`This Metabase dashboard is already declared on this account.`,
                guidance: c('Catalogue refusal')
                    .t`One live entry per dashboard and per account: a second one would be two labels for the same figures, granted separately. Rename the existing entry if the label is what has to change, or archive it before declaring this one again. Clicking again will refuse again.`,
            };
        case 'DASHBOARD_NOT_FOUND':
            return {
                title: c('Catalogue refusal').t`This entry no longer exists.`,
                guidance: c('Catalogue refusal')
                    .t`Somebody has probably archived or removed it since this screen was loaded. Reload the page to see the catalogue as it is now.`,
            };
        default:
            return null;
    }
};

/**
 * The refusal, told to the operator who has to unblock it.
 *
 * The unknown case keeps the server's sentence: an unreadable code with no detail
 * leaves the operator with nothing to report, and this lot's codes are new enough
 * that an unknown one is a real possibility rather than a defensive branch.
 */
export const catalogueRefusalOf = (error: unknown): Refusal => {
    const code = refusalCode(error);
    const known = commonRefusal(code) ?? dashboardRefusal(code);
    if (known) {
        return known;
    }
    return {
        title: c('Catalogue refusal').t`The server refused this declaration.`,
        guidance: c('Catalogue refusal')
            .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. The server's answer is below — it is what the support desk needs.`,
        detail: error instanceof Error ? error.message : String(error),
    };
};
